import React, {Fragment} from 'react'
import {connect} from 'react-redux'
import styles from './TRouteMapViewer.scss'
import blockSizes from '../../../App/blockSizes.scss'
import {renderSelectedFeature, renderHoveredFeature} from 'features/troute_show/TRouteShowPage/mapRendering'
import {MapV2} from 'common/MapV2'
import {featureTypes} from 'common/MapV2/featureTypes'
import {TrackLine} from 'common/MapV2/TrackLine'
import {TrackSnapPoint} from 'common/MapV2/TrackSnapPoint'
import {segmentPopoverClearance} from 'common/Map/mapUtils'
import {POIv3} from 'common/Map/POI'
import {PhotoClusters} from 'common/Maplet/PhotoClusters'
import {SampleGraph} from 'common/SampleGraph'
import {BlockLoading} from 'common/BlockLoading'
import {Metrics} from 'common/Metrics'
import {TrackLocation, Viewport} from 'data/records'
import {tripActions, tripSelectors} from 'data/resources/trips'
import {routeActions, routeSelectors} from 'data/resources/routes'
import {pointOfInterestSelectors} from 'data/resources/pointsOfInterest'
import {photoSelectors} from 'data/resources/photos'
import {tileServices} from 'lib/tileServices'
import {Set} from 'immutable'
import _ from 'lodash'

export class _TRouteMapViewer extends React.PureComponent {
  static defaultMetrics = ['distance', 'eleGain', 'movingTime', 'avgSpeed', 'maxSpeed']

  constructor (props) {
    super(props)

    this.state = {
      fetchPending: true,
      overlay: tileServices.rwgps,
      initialViewport: null,
      hoveredFeature: null,
      selectedFeature: null,
      zoom: 1,
      showMetricsPicker: false,
      metricsEnabled: Set(['ele'])
    }
  }

  componentDidMount () {
    const {block, loadTrip, loadRoute} = this.props
    const data = block.getData()
    const objectId = parseInt(data.get('objectId'), 10)
    const objectType = data.get('objectType')

    const loadAction = objectType === 'route' ? loadRoute : loadTrip
    loadAction(objectId).then(r => {
      this.setState({fetchPending: false, initialViewport: Viewport({bounds: r.value[objectType].boundingBox})})
    })
  }

  componentDidUpdate (prevProps, prevState) {
    if (prevState.selectedFeature !== this.state.selectedFeature) {
      this.handleSelectedFeatureChange(prevState.selectedFeature, this.state.selectedFeature)
    }

    if ((prevProps.sampleGraph !== this.props.sampleGraph) || (prevProps.showMetrics !== this.props.showMetrics)) {
      this._map._mapEngine.resizeToParentHeight()
    }
  }

  handleSelectedFeatureChange = (prevFeature, currentFeature) => {
    // less typing = more better
    const g = _.get
    // ignore switching from a feature that had a highlightSlice to a
    // SAMPLE_GRAPH_USER_SELECTION, it's just the user twiddling the sample graph
    if (g(prevFeature, 'data.highlightSlice') && g(currentFeature, 'type') === featureTypes.SAMPLE_GRAPH_USER_SELECTION) {
      return
    }
    const showPopover = this.props.block.getData().get('showPopover', true)

    // if we're going from a no-highlight-slice to yes-highlight-slice, zoom!
    if (!g(prevFeature, 'data.highlightSlice') && g(currentFeature, 'data.highlightSlice')) {
      const {troute} = this.props
      const {highlightSlice} = currentFeature.data

      const vp = Viewport.containingSlice(highlightSlice, troute.trackPoints)
      this._map._mapEngine.flyTo(vp, {padding: showPopover && segmentPopoverClearance})
      return
    }

    // if we're going from a yes-highlight-slice to no feature selected, reset zoom
    if (!currentFeature && g(prevFeature, 'data.highlightSlice')) {
      const {troute} = this.props

      const vp = Viewport({bounds: troute.boundingBox})
      this._map._mapEngine.flyTo(vp)
      return // eslint-disable-line
    }
  }

  renderSelectedFeature = renderSelectedFeature.bind(this)

  renderHoveredFeature = renderHoveredFeature.bind(this)

  onFeatureHovered = (hoveredFeature) => {
    this.setState({hoveredFeature})
  }

  clearSelectedFeature = () => {
    this.setState({selectedFeature: null})
  }

  clearHoveredFeature = () => {
    this.setState({hoveredFeature: null})
  }

  onMapSnap = ({snapPoint, snapIndex}) => {
    this.onFeatureHovered({
      type: featureTypes.POINT_GENERIC,
      data: {highlightPoint: {trackPoint: snapPoint, index: snapIndex}}
    })
  }

  hasHighlightSlice = () => {
    const {selectedFeature} = this.state
    return selectedFeature && selectedFeature.data && selectedFeature.data.highlightSlice
  }

  hasHoverSlice = () => {
    const {hoveredFeature} = this.state
    return hoveredFeature && hoveredFeature.data && hoveredFeature.data.highlightSlice
  }

  getSGHighlightSlice = () => {
    const {selectedFeature} = this.state
    if (!this.hasHighlightSlice()) {
      return undefined
    }
    // for now, the sample graph can't handle TrackLocation synthetic
    // points, so we convert to integers
    return selectedFeature.data.highlightSlice.map(TrackLocation.closestIndex)
  }

  onSGPointHighlighted = (p) => {
    const {troute} = this.props
    if (p === null) { // SG calls this with null sometimes to clear the point
      this.onFeatureHovered(null)
      return
    }
    const trackPoint = troute.trackPoints[p.index]
    this.onFeatureHovered({
      type: featureTypes.POINT_GENERIC,
      data: {highlightPoint: {trackPoint, index: p.index}}
    })
  }

  getSGHoverSlice = () => {
    const {hoveredFeature} = this.state
    if (!this.hasHoverSlice()) {
      return undefined
    }
    // for now, the sample graph can't handle TrackLocation synthetic
    // points, so we convert to integers
    return hoveredFeature.data.highlightSlice.map(TrackLocation.closestIndex)
  }

  onSliceHighlighted = (segment) => {
    this.setState({
      selectedFeature: {
        type: featureTypes.SAMPLE_GRAPH_USER_SELECTION,
        data: {highlightSlice: segment}
      },
      hoveredFeature: null
    })
  }

  onSegmentUnselected = () => {
    const {selectedFeature} = this.state
    if (selectedFeature && selectedFeature.type === featureTypes.SAMPLE_GRAPH_USER_SELECTION) {
      this.clearSelectedFeature()
    }
  }

  render () {
    const {overlay, initialViewport, zoom, hoveredFeature, metricsEnabled} = this.state
    const {block, blockProps: {metricUnits}, troute, troutePOIs, troutePhotos} = this.props
    const data = block.getData()
    const objectType = data.get('objectType')
    const sampleGraph = data.get('sampleGraph', true)
    const showPopover = data.get('showPopover', false)
    const showMetrics = data.get('showMetrics', false)
    const metricsToShow = data.get('metricsToShow', objectType === 'route'
      ? _.pull(_TRouteMapViewer.defaultMetrics, 'avgSpeed', 'maxSpeed', 'movingTime')
      : _TRouteMapViewer.defaultMetrics)

    return (
      <div className={blockSizes.normalBlock}>
        <div className={styles.trouteMapViewer}>
          {(troute && initialViewport)
            ? <Fragment>
              <MapV2
                scrollWheelZoom
                overlay={overlay}
                onChangeOverlay={overlay => this.setState({overlay})}
                ref={r => { this._map = r }}
                style={{flex: '1 0 0px', width: '100%'}}
                onViewportChanged={this.onViewportChanged}
                initialViewport={initialViewport}>
                <TrackLine trackPoints={troute.trackPoints} />
                <TrackSnapPoint hoveredFeature={hoveredFeature} troute={troute} onSnap={this.onMapSnap} onUnSnap={this.clearHoveredFeature} />
                {troutePhotos && <PhotoClusters troute={troute.set('photos', troutePhotos)} clusterStrategy={objectType} zoom={zoom} />}
                {troutePOIs && troutePOIs.map(poi => <POIv3 key={poi.id} poi={poi} photos={troutePhotos} />)}
                {this.renderSelectedFeature(showPopover)}
                {this.renderHoveredFeature()}
              </MapV2>
              {sampleGraph && <SampleGraph
                ref={r => { this._sgComponent = r }}
                plotOnly
                dragMode='select'
                segmentFormat='TrackLocation'
                height={145}
                rawPoints={troute.rawPoints}
                highlightSlice={this.getSGHighlightSlice()}
                hoverSlice={this.getSGHoverSlice()}
                onSegmentSelected={this.onSliceHighlighted}
                onSegmentUnselected={this.onSegmentUnselected}
                onPointHighlighted={this.onSGPointHighlighted}
                metricsEnabled={metricsEnabled}
                metricUnits={metricUnits} />}
              {showMetrics && <Metrics ride={troute}
                metricUnits={metricUnits}
                highlightSlice={this.getSGHighlightSlice()}
                metricsToShow={metricsToShow} />}
            </Fragment>
            : <BlockLoading height='100%' width='100%'>Loading map...</BlockLoading>}
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  const data = ownProps.block.getData()
  const objectId = parseInt(data.get('objectId'), 10)
  const objectType = data.get('objectType')
  const selectors = objectType === 'route'
    ? routeSelectors
    : tripSelectors
  const troute = selectors.find(state, objectId)

  return {
    troute,
    troutePOIs: troute && pointOfInterestSelectors.findByIds(state, troute.poiIds),
    troutePhotos: troute && photoSelectors.findByIds(state, troute.photoIds)
  }
}

const mapDispatchToProps = ({
  loadTrip: tripActions.read,
  loadRoute: routeActions.read
})

export const TRouteMapViewer = connect(
  mapStateToProps,
  mapDispatchToProps
)(_TRouteMapViewer)
